/**
 * Generic tree utility functions for converting flat arrays to hierarchical tree structures
 */

import { get } from 'lodash';
import { SortUtil } from './sort.util';

/**
 * Configuration for arrayToTree function
 */
export interface ArrayToTreeConfig {
  idProperty?: string | null;
  parentProperty?: string | null;
  levelProperty?: string | null;
  includeAncestors?: boolean;
  selectOnlyLeafNodes?: boolean;
  selectDistinctNodes?: boolean;
  suppressFirstNodeExpand?: boolean;
}

/**
 * Generic interface for tree nodes
 */
export interface TreeNode<T = unknown> {
  children?: TreeNode<T>[];

  [key: string]: unknown;
}

/**
 * Default configuration for arrayToTree
 */
const DEFAULT_CONFIG: Required<ArrayToTreeConfig> = {
  idProperty: 'id',
  parentProperty: 'parentId',
  levelProperty: 'level',
  includeAncestors: false,
  selectOnlyLeafNodes: false,
  selectDistinctNodes: false,
  suppressFirstNodeExpand: false
};

/**
 * Converts a flat array of objects with parent-child relationships into a hierarchical tree structure.
 * This implementation matches SortEntitiesUtil.arrayToTree behavior exactly.
 *
 * @template T The type of items in the array
 * @param items Flat array of items with parent-child relationships
 * @param config Configuration options for tree building
 * @returns Hierarchical tree structure with only root nodes at the top level. All nodes have a 'children' property.
 *
 * **Algorithm:**
 * 1. Add 'children: []' property to all items
 * 3. Sort by configured levelProperty (default: 'level')
 * 4. Build index map using idProperty
 * 5. For each node:
 *    - If parentProperty !== null: try to add to parent's children, otherwise add to result
 *    - If parentProperty === null: add to result (root node)
 *
 * **Root Node Detection:**
 * - Items with `parentId === null` are always root nodes
 * - Items with `parentId !== null` but parent not found in dataset become root nodes (orphans)
 *
 * @example
 * ```typescript
 * // Basic usage with default config
 * const data = [
 *   { id: '1', name: 'Root', parentId: null, level: 0 },
 *   { id: '2', name: 'Child', parentId: '1', level: 1 }
 * ];
 * const tree = arrayToTree(data);
 * // Result: [{ id: '1', name: 'Root', parentId: null, level: 0, children: [{ id: '2', ... }] }]
 *
 * // Custom configuration
 * const categories = [
 *   { categoryId: 1, categoryName: 'Electronics', parentCategoryId: null, depth: 0 },
 *   { categoryId: 2, categoryName: 'Computers', parentCategoryId: 1, depth: 1 }
 * ];
 * const tree = arrayToTree(categories, {
 *   idProperty: 'categoryId',
 *   parentProperty: 'parentCategoryId',
 *   levelProperty: 'depth'
 * });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function arrayToTree(items: any[], config: ArrayToTreeConfig = {}): TreeNode<any>[] {
  const cfg: Required<ArrayToTreeConfig> = { ...DEFAULT_CONFIG, ...config };
  const newEntities = items
    .reduce((acc, value) => {
      const entityWithChildren = {
        ...value,
        children: []
      };
      acc.push(entityWithChildren);
      return acc;
    }, [])
    .sort(SortUtil.sortByProperty(cfg.levelProperty));

  const map: Record<string, number> = {};
  const result = [];
  let node;
  let i;

  for (i = 0; i < newEntities.length; i += 1) {
    const elem = newEntities[i];
    map[get(elem, cfg.idProperty)] = i;
  }

  for (i = 0; i < newEntities.length; i += 1) {
    node = newEntities[i];
    const parentProp = get(node, cfg.parentProperty);
    if (parentProp !== null && parentProp !== undefined) {
      if (newEntities[map[parentProp]]) {
        newEntities[map[parentProp]].children.push(node);
      } else {
        result.push(node);
      }
    } else {
      result.push(node);
    }
  }

  return result;
}

/**
 * Flattens a tree structure back into a flat array.
 * Removes the 'children' property from each node.
 *
 * @template T The type of items in the tree
 * @param tree Hierarchical tree structure (output from arrayToTree)
 * @returns Flat array of items without the children property
 *
 * @example
 * ```typescript
 * const tree = [{ id: '1', name: 'Root', children: [{ id: '2', name: 'Child', children: [] }] }];
 * const flat = treeToArray(tree);
 * // Result: [{ id: '1', name: 'Root' }, { id: '2', name: 'Child' }]
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function treeToArray<T extends Record<string, any>>(tree: TreeNode<T>[]): T[] {
  const result: T[] = [];

  function traverse(nodes: TreeNode<T>[]): void {
    for (const node of nodes) {
      const { children, ...rest } = node;
      result.push(rest as unknown as T);

      if (children && Array.isArray(children) && children.length > 0) {
        traverse(children);
      }
    }
  }

  traverse(tree);
  return result;
}

/**
 * Finds a node in a tree by a predicate function.
 * Performs depth-first search through the tree.
 *
 * @template T The type of items in the tree
 * @param tree Hierarchical tree structure (output from arrayToTree)
 * @param predicate Function to test each node
 * @returns The found node or undefined
 *
 * @example
 * ```typescript
 * const tree = arrayToTree(data);
 * const node = findNodeInTree(tree, (n) => n.id === '2');
 * // Returns the node with id '2' or undefined if not found
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findNodeInTree<T extends Record<string, any>>(tree: TreeNode<T>[], predicate: (node: TreeNode<T>) => boolean): TreeNode<T> | undefined {
  function traverse(nodes: TreeNode<T>[]): TreeNode<T> | undefined {
    for (const node of nodes) {
      if (predicate(node)) {
        return node;
      }

      const children = node.children;
      if (children && Array.isArray(children) && children.length > 0) {
        const found = traverse(children);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  return traverse(tree);
}

/**
 * Gets all leaf nodes (nodes without children) from a tree.
 * A leaf node is any node where children array is empty or undefined.
 *
 * @template T The type of items in the tree
 * @param tree Hierarchical tree structure (output from arrayToTree)
 * @returns Array of leaf nodes
 *
 * @example
 * ```typescript
 * const tree = arrayToTree(data);
 * const leaves = getLeafNodes(tree);
 * // Returns all nodes without children
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getLeafNodes<T extends Record<string, any>>(tree: TreeNode<T>[]): TreeNode<T>[] {
  const result: TreeNode<T>[] = [];

  function traverse(nodes: TreeNode<T>[]): void {
    for (const node of nodes) {
      const children = node.children;

      if (!children || !Array.isArray(children) || children.length === 0) {
        result.push(node);
      } else {
        traverse(children);
      }
    }
  }

  traverse(tree);
  return result;
}

/**
 * Interface for tag data with labels
 */
export interface TagWithLabels {
  id: string;
  name: string;
  concept?: string;
  entityId?: string;
  parentId?: string;
  parentName?: string;
  color?: string;
  mandatory?: boolean;
  labels: { id: string; name: string }[];
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/**
 * Extracts all labels from tags and includes parent tags as items in the result.
 * Each label will have entityId, concept, and color from its parent tag.
 * The parent tag is also included in the result array.
 *
 * @param tags Array of tags with labels
 * @returns Flat array containing both parent tags and their labels with parent information
 *
 * @example
 * ```typescript
 * const tags = [
 *   {
 *     "id": "107e3049-7329-4a7a-aacc-db016be1667a",
 *     "name": "TEST",
 *     "concept": "SITE",
 *     "entityId": "5d78f1a1-b5d9-4b71-94b1-cbaa0b01fa43",
 *     "color": "#76A9FF",
 *     "mandatory": false,
 *     "labels": [
 *       { "id": "1b7f829e-14b7-497e-99d4-ac33b77978b6", "name": "TEST_1" },
 *       { "id": "ec1443c5-d8fd-4782-9dea-d43b6a729eea", "name": "TEST_2" }
 *     ]
 *   }
 * ];
 * const items = extractLabelsWithParentInfo(tags);
 * // Result: [
 * //   {
 * //     id: '107e3049-7329-4a7a-aacc-db016be1667a',
 * //     name: 'TEST',
 * //     concept: 'SITE',
 * //     entityId: '5d78f1a1-b5d9-4b71-94b1-cbaa0b01fa43',
 * //     color: '#76A9FF'
 * //   },
 * //   {
 * //     id: '1b7f829e-14b7-497e-99d4-ac33b77978b6',
 * //     name: 'TEST_1',
 * //     entityId: '5d78f1a1-b5d9-4b71-94b1-cbaa0b01fa43',
 * //     concept: 'SITE',
 * //     color: '#76A9FF'
 * //   },
 * //   {
 * //     id: 'ec1443c5-d8fd-4782-9dea-d43b6a729eea',
 * //     name: 'TEST_2',
 * //     entityId: '5d78f1a1-b5d9-4b71-94b1-cbaa0b01fa43',
 * //     concept: 'SITE',
 * //     color: '#76A9FF'
 * //   }
 * // ]
 * ```
 */
export function extractLabelsWithParentInfo(tags: TagWithLabels[]): TagWithLabels[] {
  const result: TagWithLabels[] = [];

  for (const tag of tags) {
    // Add the parent tag as an item
    result.push(tag);

    // Add all labels with parent information
    const labels = tag.labels || [];
    for (const label of labels) {
      result.push({
        id: label.id,
        name: label.name,
        entityId: tag.entityId,
        parentId: tag.id,
        parentName: tag.name,
        concept: tag.concept,
        color: tag.color
      } as TagWithLabels);
    }
  }

  return result;
}
